var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { HomeContentBackground, PageLoading } from '@lean-body/components/organisms';
import { HomeCountLessonBadge, HomeDailyLessonVolumeChip } from '@lean-body/components/molecules';
import { HomeDailyLessonMap } from '../organisms/home_daily_lesson_map';
import { HomeBeginnerMissionLink } from '../molecules/home_beginner_mission_link';
import { colorForVolumeNumber } from '@lean-body/src/domain/daily_lesson_volume_colors';
import { StreakCompletionResult, StreakToastMessageMap, CountLessonCoachVideoPlayback, } from '@lean-body/src/domain';
import { SessionStorage } from '@lean-body/src/infra';
import { DailyTasksPageTaskName, generateLessonQueryParams, hideAutoPlay, LessonType, routes, } from '@lean-body/src/route';
import { isAndroidApp, useXlMediaQuery } from '@lean-body/src/util';
import { css, StyleSheet } from 'aphrodite';
import { LocalStorage } from '../../infra/local_storage';
import { HOME_SECTION_MARGIN_BOTTOM, ZIndex } from '../constants';
import { MediaBreakPointUp, Sizes, drawerWidth } from '../styles';
import { FIXED_FOOTER_HEIGHT } from '../organisms/fixed_footer';
import { DailyTasksList } from '../organisms/daily_tasks_list';
import { DailyTasksCompactList } from '../organisms/daily_tasks_compact_list';
import { DailyTasksProgress } from '../organisms/daily_tasks_progress';
import { WebViewCallbackHandlers, callWebViewHandler, } from '@lean-body/src/interfaces';
import { HomeDialogGroup } from '../organisms/home_dialogs';
import { CancelUnsubscribeBanner } from '../organisms/cancel_unsubscribe_banner';
import { TV_CONNECTION_BANNER_XL_HEIGHT, TvConnectionBanner } from '../organisms/tv_connection_banner';
import { BannerSection } from '../organisms/banner_section';
import { buildHomeUndiagnosedPreviewDailyLesson } from '@lean-body/src/domain/daily_lesson';
import { DailyLessonChangeLoadingView } from '../molecules/daily_lesson_change_loading_view';
import { GoalMatchingAssessmentPageQuery } from './main_goal_matching_assessment';
import { useToast } from '../hooks/use_toast';
import { ToastMessage } from '../molecules/toast';
import { StreakRecordOverlay } from '../organisms/streak_record_overlay';
import { useStreakCompletionResult } from '../hooks/use_streak_completion_result';
export const LIST_ITEM_MARGIN_RIGHT = 10;
const SWIPER_AUTOPLAY_DELAY = 4000;
export const HOME_MAP_SCROLL_ELEMENT_ID = 'homeMapScroll';
export const HomePage = ({ apiClient, twitterService, accountService, tracker, config, profile, setProfile, me, setMe, currentStreak, isTvBannerVisible, onCloseTvBanner, }) => {
    const navigate = useNavigate();
    const localStorage = new LocalStorage();
    const today = new Date();
    const isXL = useXlMediaQuery();
    const [isLoading, setIsLoading] = useState(true);
    const [banners, setBanners] = useState([]);
    const [dailyLesson, setDailyLesson] = useState();
    const [beginnerMission, setBeginnerMission] = useState(null);
    const [isFirstHomeVisit, setIsFirstHomeVisit] = useState(false);
    const [isBeginnerMissionModalOpen, setIsBeginnerMissionModalOpen] = useState(false);
    const [hasDismissedAutoBeginnerMission, setHasDismissedAutoBeginnerMission] = useState(false);
    const [shouldShowCompletedBeginnerMission, setShouldShowCompletedBeginnerMission] = useState(false);
    const isUndiagnosedHome = dailyLesson == null;
    const displayDailyLesson = dailyLesson !== null && dailyLesson !== void 0 ? dailyLesson : buildHomeUndiagnosedPreviewDailyLesson(config);
    const [dailyTasks, setDailyTasks] = useState();
    // 初回診断済みだが目的・運動時間が未設定の再診断対象（今日やるコマの吹き出しを再診断へ置換／診断は体重から）
    const isRediagnosisTarget = dailyTasks != null && dailyTasks.needsRediagnosis;
    const [releaseNotificationDialog, setReleaseNotificationDialog] = useState(null);
    const sessionStorage = new SessionStorage();
    const hasAndroidStepccountPermission = sessionStorage.loadAndroidStepcountPermission();
    const hasRecordedMeal = localStorage.loadHasRecordedMeal;
    const [mealAboutDialogType, setMealAboutDialogType] = useState(null);
    const [isWeightHidden, setIsWeightHidden] = useState(profile.isWeightHidden);
    const [healthConnectFetchRequestId, setHealthConnectFetchRequestId] = useState(0);
    const { showToast, pendingToasts, flushPendingToasts } = useToast();
    const { streakCompletionResult: pendingStreakCompletionResult, resetStreakCompletionResult } = useStreakCompletionResult();
    const [streakCompletionResult, setStreakCompletionResult] = useState(null);
    const [isDailyLessonChanging, setIsDailyLessonChanging] = useState(false);
    const popoverLayerRef = useRef(null);
    const checkedFirstHomeVisitFor = useRef(null);
    const shouldSkipInitialHomeDayFetch = useRef(true);
    const isMountedRef = useRef(true);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        apiClient
            .fetchBeginnerMissions()
            .then((mission) => {
            if (!isMountedRef.current)
                return;
            setBeginnerMission(mission);
            if (mission.items.length > 0) {
                // 1日1回の自動表示とは別に、前回取得時の未達成から全達成へ変わった場合だけ表示する。
                const previousAchievedCount = localStorage.loadBeginnerMissionAchievedCount();
                localStorage.saveBeginnerMissionAchievedCount(mission.achievedCount);
                setShouldShowCompletedBeginnerMission(mission.hasJustCompleted(previousAchievedCount));
            }
            if (checkedFirstHomeVisitFor.current !== me.id) {
                // 取得失敗時に同日の再読み込みで自動表示を再試行できるよう、成功後に訪問日を保存する。
                checkedFirstHomeVisitFor.current = me.id;
                const today = dayjs().format('YYYY-MM-DD');
                const isFirst = localStorage.loadBeginnerMissionHomeVisitDate() !== today;
                localStorage.saveBeginnerMissionHomeVisitDate(today);
                const shouldSkipAutoOpen = sessionStorage.consumeSkipBeginnerMissionDialogAutoOpen();
                setIsFirstHomeVisit(isFirst && !shouldSkipAutoOpen);
            }
        })
            .catch(() => {
            // ミッションAPIの失敗時もHOMEの他の機能は表示する。
            if (isMountedRef.current && checkedFirstHomeVisitFor.current !== me.id) {
                setIsFirstHomeVisit(false);
            }
        });
        Promise.all([
            apiClient.fetchMyDailyLesson(),
            apiClient.fetchDailyTasks(today),
            accountService.fetchMe({ nocache: true }),
            apiClient.getHomeBanners(),
            apiClient.fetchDialog(),
        ])
            .then(([fetchedDailyLesson, fetchDailyTasks, fetchedMe, fetchBanners, fetchDialog]) => {
            if (!isMountedRef.current)
                return;
            setDailyLesson(fetchedDailyLesson);
            setDailyTasks(fetchDailyTasks);
            setMe(fetchedMe);
            setBanners(fetchBanners);
            setReleaseNotificationDialog(fetchDialog);
        })
            .finally(() => {
            if (!isMountedRef.current)
                return;
            setIsLoading(false);
        });
        // zustandに保存された連続記録(overlay)
        if (pendingStreakCompletionResult) {
            setStreakCompletionResult(pendingStreakCompletionResult);
            resetStreakCompletionResult();
        }
        // zustandに保存された連続記録(トースト)
        if (pendingToasts.length) {
            const availableToasts = new Set(Object.values(ToastMessage));
            pendingToasts.forEach((toast) => {
                if (availableToasts.has(toast)) {
                    showToast(toast);
                }
            });
            flushPendingToasts();
        }
        // ヘルスコネクト経由の連続記録
        if (isAndroidApp()) {
            const handleHealthConnect = (streakCompletionResult) => {
                if (!streakCompletionResult)
                    return;
                const completionResult = new StreakCompletionResult(streakCompletionResult, config);
                const toastMessage = StreakToastMessageMap[completionResult.sourceType];
                showToast(toastMessage);
                if (completionResult.isFirstDailyCompletion) {
                    setStreakCompletionResult((prev) => {
                        if (prev)
                            return; // 他の処理で先にsetされていたら何もしない
                        return completionResult;
                    });
                }
            };
            callWebViewHandler(WebViewCallbackHandlers.sendStepFromHealthConnect).then((v) => {
                if (!v || !isMountedRef.current)
                    return;
                handleHealthConnect(v.steps);
                handleHealthConnect(v.workout);
                setHealthConnectFetchRequestId((prev) => prev + 1);
            });
            callWebViewHandler(WebViewCallbackHandlers.sendWeightAndFatBodyFromHealthConnect).then((v) => {
                if (!v || !isMountedRef.current)
                    return;
                handleHealthConnect(v);
                setHealthConnectFetchRequestId((prev) => prev + 1);
            });
        }
    }, []);
    useEffect(() => {
        // 初回マウント時のリクエストは上の useEffect と重複するため、1回だけスキップする
        if (shouldSkipInitialHomeDayFetch.current) {
            shouldSkipInitialHomeDayFetch.current = false;
            return;
        }
        apiClient.fetchDailyTasks(today).then((v) => {
            if (!isMountedRef.current)
                return;
            setDailyTasks(v);
        });
    }, [healthConnectFetchRequestId]);
    const onCloseStreakOverlay = () => {
        setStreakCompletionResult(null);
    };
    const onClickTaskItemAndroid = (taskName) => {
        if (hasAndroidStepccountPermission) {
            navigate(routes.DAILY_TASKS.compile({ taskName }));
        }
        else {
            window.flutter_inappwebview.callHandler(WebViewCallbackHandlers.requestHealthConnectPermission);
        }
    };
    const navigateMealRecordDetail = () => {
        navigate(routes.DAILY_TASKS.compile({ taskName: DailyTasksPageTaskName.meal }));
    };
    const openMealAboutDialog = () => {
        if (!localStorage.loadHasRecordedMeal) {
            setMealAboutDialogType('meal-task');
            return true;
        }
        if (!localStorage.loadHasRecordedMealV2) {
            setMealAboutDialogType('ai-input');
            return true;
        }
        return false;
    };
    const onClickTaskItem = (taskName) => {
        if (taskName === DailyTasksPageTaskName.meal && openMealAboutDialog())
            return;
        navigate(routes.DAILY_TASKS.compile({ taskName }));
    };
    const onClickMealRecordDetail = () => {
        if (openMealAboutDialog())
            return;
        navigateMealRecordDetail();
    };
    const onCloseMealTaskAboutDialog = () => {
        setMealAboutDialogType(null);
    };
    const onSuccessMealTaskAboutDialog = () => {
        setMealAboutDialogType(null);
        navigateMealRecordDetail();
    };
    const onToggleWeightDisplay = () => __awaiter(void 0, void 0, void 0, function* () {
        const nextHidden = !isWeightHidden;
        // 先にローカル state を切り替えて即時反映（失敗時もリバートしない）
        setIsWeightHidden(nextHidden);
        // 成功時はサーバの最新プロフィールでグローバル state も同期する（別タブから戻ってきた時等に維持するため）
        const updatedProfile = yield apiClient.UpdateWeightDisplay(nextHidden);
        setProfile(updatedProfile);
    });
    const onClickDailyLessonVolume = () => {
        navigate(routes.DAILY_START.compile());
    };
    const onClickCountLessonBadge = () => {
        CountLessonCoachVideoPlayback.prepareFromUserGesture();
        navigate(routes.COUNT_LESSONS.compile());
    };
    const onStartDailyLessonDay = (day) => {
        const url = routes.LESSON.compile({ type: LessonType.normal, slug: day.dailyLessonDayLesson.lesson.slug });
        // HOME（今日のデイリーレッスン）からの再生は自動再生を無効化する
        const params = hideAutoPlay(generateLessonQueryParams(day.dailyLessonDayLesson.id));
        navigate(`${url}${params}`);
    };
    const onClickDiagnosisStart = () => {
        // 診断経験のある再診断対象はマイページ目標設定と同様に体重から開始する
        const assessmentPath = isRediagnosisTarget
            ? `${routes.GOAL_MATCHING_ASSESSMENT.compile()}?${GoalMatchingAssessmentPageQuery.change}=1`
            : routes.GOAL_MATCHING_ASSESSMENT.compile();
        navigate(assessmentPath);
    };
    // HOME の吹き出しからのチェンジは変更理由を尋ねず、変更のみ実行する
    const onChangeDailyLessonDay = (day) => __awaiter(void 0, void 0, void 0, function* () {
        setIsDailyLessonChanging(true);
        try {
            const newLesson = yield apiClient.changeDailyLessonLesson({ courseDaysLessonID: day.dailyLessonDayLesson.id });
            if (newLesson != null && isMountedRef.current) {
                setDailyLesson((prev) => (prev != null ? applyChangedDailyLessonDayLesson(prev, newLesson) : prev));
            }
        }
        finally {
            if (isMountedRef.current) {
                setIsDailyLessonChanging(false);
            }
        }
    });
    // チェンジ API が返す新レッスンを該当日に差し込み、再取得せずにマップへ反映する
    const applyChangedDailyLessonDayLesson = (dailyLesson, newLesson) => {
        const targetDay = dailyLesson.volumes
            .flatMap((volume) => volume.days)
            .find((day) => day.id === newLesson.dailyLessonDayId);
        if (targetDay != null) {
            targetDay.dailyLessonDayLesson = newLesson;
        }
        return Object.assign(Object.create(Object.getPrototypeOf(dailyLesson)), dailyLesson);
    };
    const displayDailyLessonVolume = displayDailyLesson.volumes.find((volume) => volume.isCurrentVolume);
    const isSample = dailyTasks != null && isUndiagnosedHome;
    const isBeginnerMissionWithinPeriod = !!(beginnerMission === null || beginnerMission === void 0 ? void 0 : beginnerMission.isWithinHomeDisplayPeriod);
    const isBeginnerMissionVisible = !isUndiagnosedHome && isBeginnerMissionWithinPeriod;
    const shouldAutoShowBeginnerMission = isFirstHomeVisit &&
        !isUndiagnosedHome &&
        isBeginnerMissionWithinPeriod &&
        !!(beginnerMission === null || beginnerMission === void 0 ? void 0 : beginnerMission.shouldShowOnLogin) &&
        beginnerMission.items.some((item) => !item.isAchieved) &&
        !hasDismissedAutoBeginnerMission;
    const shouldAutoShowCompletedBeginnerMission = !isUndiagnosedHome && isBeginnerMissionWithinPeriod && shouldShowCompletedBeginnerMission;
    const isBeginnerMissionDialogOpen = isBeginnerMissionModalOpen || shouldAutoShowBeginnerMission || shouldAutoShowCompletedBeginnerMission;
    const onCloseBeginnerMissionDialog = () => {
        setIsBeginnerMissionModalOpen(false);
        setHasDismissedAutoBeginnerMission(true);
        setShouldShowCompletedBeginnerMission(false);
    };
    if (isLoading) {
        return React.createElement(PageLoading, null);
    }
    return (React.createElement("div", { className: css(styles.container) },
        React.createElement("div", { className: css(styles.content) },
            React.createElement(HomeContentBackground, null,
                React.createElement("div", { className: css(styles.homeViewport) },
                    React.createElement("div", { className: css(styles.homeMapScrollClip) },
                        React.createElement("div", { id: HOME_MAP_SCROLL_ELEMENT_ID, className: css(styles.homeMapScroll) },
                            React.createElement("div", { className: css(styles.homeMapScrollInsetTop), "aria-hidden": true }),
                            React.createElement("div", { className: css(styles.contentInnerSection, styles.contentInnerSectionDailyStart) }, displayDailyLesson && (React.createElement("div", { className: css(styles.dailyStartMapSlot) },
                                React.createElement(HomeDailyLessonMap, { dailyLesson: displayDailyLesson, onStartDay: onStartDailyLessonDay, onChangeDay: onChangeDailyLessonDay, onDiagnosisStart: onClickDiagnosisStart, isRediagnosisTarget: isRediagnosisTarget, popoverLayerRef: popoverLayerRef })))),
                            React.createElement("div", { className: css(styles.homeMapScrollInsetBottom), "aria-hidden": true }))),
                    dailyTasks && (React.createElement("div", { className: css(styles.homePinnedTasks) }, isXL ? (React.createElement("div", { className: css(styles.homePinnedTasksXLScroll) },
                        React.createElement("div", { className: css(styles.homePinnedTasksXLScrollBody) },
                            React.createElement("div", { className: css(styles.homePinnedTasksXLScrollInsetTop), "aria-hidden": true }),
                            React.createElement("div", { className: css(styles.sectionTitle) },
                                "\u4ECA\u65E5\u306E\u30BF\u30B9\u30AF",
                                !isSample && React.createElement(DailyTasksProgress, { dailyTasks: dailyTasks })),
                            React.createElement(DailyTasksList, { showMealTaskBadge: !hasRecordedMeal && !isSample, dailyTasks: dailyTasks, isSample: isSample, onClickAndroidTaskItem: onClickTaskItemAndroid, onClickTaskItem: onClickTaskItem, onClickMealRecordDetail: onClickMealRecordDetail, onClickSample: onClickDiagnosisStart, classObject: styles.tasksContainer, hasAndroidStepccountPermission: hasAndroidStepccountPermission, isWeightHidden: isWeightHidden, onToggleWeightDisplay: onToggleWeightDisplay })))) : (React.createElement("div", { className: css(styles.homePinnedTasksInner) },
                        React.createElement(DailyTasksCompactList, { dailyTasks: dailyTasks, isSample: isSample, onClickAndroidTaskItem: onClickTaskItemAndroid, onClickTaskItem: onClickTaskItem, onClickMealRecordDetail: onClickMealRecordDetail, onClickSample: onClickDiagnosisStart, hasAndroidStepccountPermission: hasAndroidStepccountPermission, isWeightHidden: isWeightHidden, onToggleWeightDisplay: onToggleWeightDisplay }))))),
                    React.createElement("div", { className: css(styles.homePinnedTop, isTvBannerVisible && styles.homePinnedTopWithTvBanner) },
                        React.createElement(CancelUnsubscribeBanner, { me: me, tracker: tracker }),
                        !!banners.length && (React.createElement(BannerSection, { tracker: tracker, banners: banners, autoplayDelay: SWIPER_AUTOPLAY_DELAY, classObject: styles.homePinnedTopInteractive })),
                        React.createElement("div", { className: css(styles.homePinnedMapActions) },
                            !isXL && isBeginnerMissionVisible && (React.createElement("div", { className: css(styles.homePinnedMissionIcons, !banners.length && styles.homePinnedMissionIconsWithoutBanner) },
                                React.createElement(HomeBeginnerMissionLink, { onClick: () => setIsBeginnerMissionModalOpen(true) }))),
                            displayDailyLessonVolume && (React.createElement("div", { className: css(styles.homeMapVolumeChipRow) },
                                React.createElement("div", { className: css(styles.homeMapVolumeChipColumn) },
                                    React.createElement(HomeDailyLessonVolumeChip, { volumeNumber: displayDailyLessonVolume.volumeNumber, volumeColor: colorForVolumeNumber(displayDailyLessonVolume.volumeNumber), onClick: isUndiagnosedHome ? undefined : () => onClickDailyLessonVolume(), classObject: styles.homeMapVolumeChip }),
                                    React.createElement(HomeCountLessonBadge, { onClick: onClickCountLessonBadge, classObject: styles.homeCountLessonBadge })))),
                            isXL && isBeginnerMissionVisible && (React.createElement(HomeBeginnerMissionLink, { classObject: styles.homeBeginnerMissionLink, onClick: () => setIsBeginnerMissionModalOpen(true) })))),
                    isXL && (React.createElement(TvConnectionBanner, { config: config, isVisible: isTvBannerVisible, onClose: onCloseTvBanner, classObject: styles.tvConnectionBannerOverlay }))))),
        createPortal(React.createElement("div", { ref: popoverLayerRef, className: css(styles.homeDailyLessonPopoverLayer) }), document.body),
        isDailyLessonChanging && React.createElement(DailyLessonChangeLoadingView, null),
        streakCompletionResult ? (React.createElement(StreakRecordOverlay, { streakCompletionResult: streakCompletionResult, onClose: onCloseStreakOverlay })) : (React.createElement(HomeDialogGroup, { isMealTaskAboutDialogOpen: mealAboutDialogType === 'meal-task', isMealAIInputAboutDialogOpen: mealAboutDialogType === 'ai-input', releaseNotificationDialog: releaseNotificationDialog, twitterService: twitterService, currentStreak: currentStreak, onCloseMealTaskAboutDialog: onCloseMealTaskAboutDialog, onSuccessMealTaskAboutDialog: onSuccessMealTaskAboutDialog, beginnerMission: beginnerMission, isBeginnerMissionDialogOpen: isBeginnerMissionDialogOpen, onCloseBeginnerMissionDialog: onCloseBeginnerMissionDialog }))));
};
const styles = StyleSheet.create({
    container: {
        display: 'flex',
        flexDirection: 'column',
        [MediaBreakPointUp.XL]: {
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        marginRight: 0,
        [MediaBreakPointUp.XL]: {
            flex: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden',
        },
    },
    homeViewport: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        [MediaBreakPointUp.XL]: {
            flex: 1,
            minHeight: 0,
            height: '100%',
            maxHeight: '100%',
            overflow: 'hidden',
        },
    },
    homeDailyLessonPopoverLayer: {
        position: 'absolute',
        top: 0,
        left: 0,
        zIndex: ZIndex.HomePositionFixed,
        pointerEvents: 'none',
    },
    homeMapScrollClip: {
        position: 'relative',
        zIndex: 0,
        [MediaBreakPointUp.XL]: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'hidden',
        },
    },
    homeBeginnerMissionLink: {
        position: 'absolute',
        top: 134,
        left: 48,
    },
    homeMapVolumeChipRow: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: 16,
        [MediaBreakPointUp.XL]: {
            width: 'calc(100% - 645px)',
            minWidth: 647,
            justifyContent: 'flex-start',
            paddingLeft: 48,
            paddingRight: 0,
        },
    },
    homeMapVolumeChip: {
        pointerEvents: 'auto',
    },
    // チップとカウントレッスンバッジを左端揃えで縦に並べる
    homeMapVolumeChipColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        // デザイン上のチップ下端〜カード上端は22px。画像上部に時計イラストのはみ出し分(約15px)を含むため差分を指定する
        gap: 8,
    },
    homeCountLessonBadge: {
        pointerEvents: 'auto',
        // スマホとiPadはVolチップの右下ではなく、画面左に置く。PCはチップの下
        position: 'absolute',
        left: 0,
        top: 0,
        [MediaBreakPointUp.XL]: {
            position: 'static',
        },
    },
    homeMapScroll: {
        width: '100%',
        [MediaBreakPointUp.XL]: {
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarGutter: 'auto',
            '-ms-overflow-style': 'none',
            scrollbarWidth: 'none',
            '::-webkit-scrollbar': {
                width: 0,
                height: 0,
            },
        },
    },
    homeMapScrollInsetTop: {
        flexShrink: 0,
        height: 114,
    },
    homeMapScrollInsetBottom: {
        flexShrink: 0,
        height: 200,
        [MediaBreakPointUp.MD]: {
            height: 250,
        },
        [MediaBreakPointUp.XL]: {
            height: 200,
        },
    },
    homePinnedTop: {
        position: 'fixed',
        top: Sizes.MobileHeaderHeight,
        left: 0,
        right: 0,
        zIndex: ZIndex.HomePositionFixed,
        pointerEvents: 'none',
        [MediaBreakPointUp.MD]: {
            left: drawerWidth.md,
        },
        [MediaBreakPointUp.XL]: {
            position: 'absolute',
            top: 0,
            left: 0,
        },
    },
    homePinnedTopWithTvBanner: {
        top: Sizes.MobileHeaderHeight + TV_CONNECTION_BANNER_XL_HEIGHT,
    },
    homePinnedTopInteractive: {
        pointerEvents: 'auto',
    },
    homePinnedMapActions: {
        position: 'relative',
    },
    homePinnedMissionIcons: {
        position: 'absolute',
        top: 93,
        left: 0,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'none',
    },
    homePinnedMissionIconsWithoutBanner: {
        top: 40,
    },
    tvConnectionBannerOverlay: {
        [MediaBreakPointUp.XL]: {
            pointerEvents: 'auto',
        },
    },
    homePinnedTasks: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: FIXED_FOOTER_HEIGHT,
        zIndex: ZIndex.HomePositionFixed,
        pointerEvents: 'none',
        padding: '0 16px 12px',
        [MediaBreakPointUp.MD]: {
            bottom: 0,
            left: drawerWidth.md,
        },
        [MediaBreakPointUp.XL]: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 'auto',
            right: 30,
            width: 'calc(100% - 677px)',
            maxWidth: 555,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
        },
    },
    homePinnedTasksInner: {
        pointerEvents: 'auto',
    },
    homePinnedTasksXLScroll: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
        paddingRight: 16,
    },
    homePinnedTasksXLScrollBody: {
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        scrollbarGutter: 'auto',
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': {
            width: 0,
            height: 0,
        },
        boxSizing: 'border-box',
        paddingBottom: 30,
        pointerEvents: 'auto',
    },
    homePinnedTasksXLScrollInsetTop: {
        flexShrink: 0,
        height: 114,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        lineHeight: '160%',
        letterSpacing: '1.28px',
        margin: '0 20px 10px',
        paddingTop: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        [MediaBreakPointUp.MD]: {
            margin: '0 0 10px',
        },
        [MediaBreakPointUp.XL]: {
            margin: '0 0 16px',
        },
    },
    contentInnerSection: {
        position: 'relative',
        paddingBottom: HOME_SECTION_MARGIN_BOTTOM,
    },
    dailyStartMapSlot: {
        position: 'relative',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        boxSizing: 'border-box',
        [MediaBreakPointUp.XL]: {
            width: 'calc(100% - 645px)',
            minWidth: 647,
        },
    },
    contentInnerSectionDailyStart: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        [MediaBreakPointUp.XL]: {
            flex: 1,
            maxWidth: '100%',
            boxSizing: 'border-box',
        },
    },
    tasksContainer: {
        marginLeft: 24,
        marginRight: 24,
        [MediaBreakPointUp.MD]: {
            marginLeft: 0,
            marginRight: 0,
        },
    },
});
